/**
 * AI CONTEXT
 * Purpose: primary front-desk operational dashboard (attendance + ops panels).
 * Key deps: AdminViewContext, ClassScheduleTimeline, AttendanceView, task/notes/event widgets.
 * Referenced by: App route ROUTES.FRONT_DESK_VIEW.
 * Importance: real-time day-of command center.
 * Refactor: extract panel layout config + persist splitter preferences.
 */
import { Splitter } from "antd"
import { useEffect, useRef } from "react"
import styled from "styled-components"

import DateDisplay from "#blocks/DateDisplay"
import EmptyPage from "#blocks/EmptyPage"
import Flex from "#blocks/Flex"
import Page from "#blocks/Page"
import PageSpinner from "#blocks/PageSpinner"
import { HEADER_HEIGHT } from "#components/Header"
import { useAdminViewContext } from "#contexts/AdminViewContextProvider"
import { RESOURCES } from "#customTypes/composedTypes/authTypes"
import { useResourcePermissions } from "#hooks/newHooks/useHasPermission"
import AttendanceView from "#views/AdminView/AttendanceView/AttendanceView"
import ClassScheduleTimeline from "#views/AdminView/ClassScheduleTimeline/ClassScheduleTimeline"
import EmployeeList from "#views/AdminView/EmployeeList"
import EventList from "#views/AdminView/EventList"
import ExceptionList from "#views/AdminView/ExceptionList"
import QuickNotes from "#views/AdminView/QuickNotes/QuickNotes"
import StudentTaskList from "#views/AdminView/StudentTaskList/StudentTaskList"

const SLeftSideContainer = styled(Flex)`
  margin-left: auto;
  width: 200px;
  max-height: calc(100vh - 50px);
  align-items: flex-end;
  flex-shrink: 0;
  flex-direction: column;
  padding-left: 120px;
  position: relative;
`

const SMainContainer = styled(Flex)`
  padding: 0px 20px;
  max-height: "calc(100vh - 150px)";
  width: "calc(100vw - 615px - 40px)";
  overflow: hidden;
  justify-content: center;
  flex-grow: 1;
`

const SRightSideContainer = styled(Flex)`
  width: 415px;
  flex-shrink: 0;
  overflow: hidden;
  flex-direction: column;
`

// Parked panel. The JSX that mounts it is commented out below.
export const SSecondRightSideContainer = styled(Flex)`
  width: 370px;
  flex-shrink: 0;
  overflow: hidden;
  flex-direction: column;
`

const SHeader = styled(Flex)`
  height: 100px;
  align-items: center;
  width: 760px;
  padding: 40px 0px;
  position: relative;
`

const SECTION_GAP = 6

const RightSideContainer = () => {
  return (
    <Flex
      col
      gap={20}
      grow
      style={{
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        minWidth: 415,
        paddingRight: 4,
      }}
    >
      <Splitter layout="vertical">
        <Splitter.Panel>
          <Flex
            style={{
              height: "100%",
              paddingBottom: SECTION_GAP,
              paddingTop: SECTION_GAP,
            }}
          >
            <StudentTaskList />
          </Flex>
        </Splitter.Panel>
        <Splitter.Panel>
          <Flex
            style={{
              height: "100%",
              paddingTop: SECTION_GAP,
              paddingBottom: SECTION_GAP,
            }}
          >
            <ExceptionList />
          </Flex>
        </Splitter.Panel>
        <Splitter.Panel>
          <Flex
            gap={24}
            style={{
              height: "100%",
              paddingTop: SECTION_GAP,
              paddingBottom: SECTION_GAP,
            }}
          >
            <QuickNotes />
          </Flex>
        </Splitter.Panel>
      </Splitter>
    </Flex>
  )
}

// Parked panel (events + employees). Mounted only from the commented JSX below.
export const SecondRightSideContainer = () => {
  return (
    <Flex
      col
      gap={20}
      grow
      style={{
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        paddingRight: 4,
      }}
    >
      <Splitter layout="vertical">
        <Splitter.Panel>
          <Flex
            style={{
              height: "100%",
              paddingBottom: SECTION_GAP,
              paddingTop: SECTION_GAP,
            }}
          >
            <EventList />
          </Flex>
        </Splitter.Panel>
        <Splitter.Panel>
          <Flex
            style={{
              height: "100%",
              paddingTop: SECTION_GAP,
              paddingBottom: SECTION_GAP,
            }}
          >
            <EmployeeList />
          </Flex>
        </Splitter.Panel>
      </Splitter>
    </Flex>
  )
}

const LOADING_DELAY = 1000

const SuperView = () => {
  const firstLoad = useRef(false)
  const { selectedDate, setSelectedDate, isReady } = useAdminViewContext()

  const { canRead } = useResourcePermissions(RESOURCES.FRONT_DESK_VIEW)

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null
    if (isReady && !firstLoad.current) {
      timeout = setTimeout(() => {
        firstLoad.current = true
      }, LOADING_DELAY)
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [isReady])

  if (!canRead) {
    return (
      <Page>
        <EmptyPage />
      </Page>
    )
  }

  if (!isReady && !firstLoad.current) {
    return <PageSpinner />
  }

  return (
    <Page>
      <Flex
        style={{
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          maxWidth: "100vw",
          justifyContent: "space-between",
        }}
        grow
      >
        <SLeftSideContainer>
          <ClassScheduleTimeline />
        </SLeftSideContainer>

        <SMainContainer>
          <Flex col style={{ width: "100%" }}>
            <SHeader gap={40}>
              <DateDisplay
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
              />
            </SHeader>
            <AttendanceView />
          </Flex>
        </SMainContainer>

        <SRightSideContainer>
          <RightSideContainer />
        </SRightSideContainer>

        {/* <SSecondRightSideContainer>
          <SecondRightSideContainer />
        </SSecondRightSideContainer> */}
      </Flex>
    </Page>
  )
}

export default SuperView
